import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/sonner";

// Update module progress in the database with quiz score validation
export const updateModuleProgress = async (
  userId: string, 
  moduleId: string, 
  courseId: string,
  progress: number,
  quizScore?: number,
  quizPassed?: boolean
): Promise<boolean> => {
  try {
    // First check if a record already exists
    const { data: existingRecord } = await supabase
      .from("user_module_progress")
      .select("id, progress")
      .eq("user_id", userId)
      .eq("module_id", moduleId)
      .maybeSingle();

    // Only allow 100% completion if quiz is passed with 85%+
    let finalProgress = progress;
    if (progress === 100) {
      if (!quizPassed || (quizScore !== undefined && quizScore < 85)) {
        finalProgress = 90; // Cap at 90% until quiz is passed
        toast.warning("Complete the quiz with 85% or higher to finish this module");
      }
    }

    if (existingRecord) {
      // Update existing record
      if (existingRecord.progress !== finalProgress) {
        const { error } = await supabase
          .from("user_module_progress")
          .update({ 
            progress: finalProgress,
            completed_at: finalProgress === 100 ? new Date().toISOString() : null
          })
          .eq("id", existingRecord.id);

        if (error) throw error;
      }
    } else {
      // Insert new record
      const { error } = await supabase
        .from("user_module_progress")
        .insert({
          user_id: userId,
          module_id: moduleId,
          course_id: courseId,
          progress: finalProgress,
          completed_at: finalProgress === 100 ? new Date().toISOString() : null
        });

      if (error) throw error;
    }

    // Log activity for progress tracking
    await logModuleActivity(userId, moduleId, courseId, 'module_complete', {
      progress: finalProgress,
      quiz_score: quizScore,
      quiz_passed: quizPassed
    });

    // Update course enrollment if module is completed with passing quiz
    if (finalProgress === 100 && quizPassed) {
      await updateCourseProgress(userId, courseId);
    }

    return true;
  } catch (error) {
    console.error("Error updating module progress:", error);
    toast.error("Failed to update progress");
    return false;
  }
};

// Log module activity for insights generation
export const logModuleActivity = async (
  userId: string,
  moduleId: string,
  courseId: string,
  activityType: string,
  metadata: Record<string, any> = {},
  timeSpent: number = 0
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('module_activity_logs')
      .insert({
        user_id: userId,
        module_id: moduleId,
        course_id: courseId,
        activity_type: activityType,
        time_spent: timeSpent,
        metadata: metadata
      });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error("Error logging module activity:", error);
    return false;
  }
};

// Validate quiz completion before marking module complete
export const completeModuleWithQuiz = async (
  userId: string,
  moduleId: string,
  courseId: string,
  quizScore: number,
  totalQuestions: number
): Promise<boolean> => {
  const scorePercentage = (quizScore / totalQuestions) * 100;
  const quizPassed = scorePercentage >= 85;

  if (!quizPassed) {
    toast.error(`Quiz score of ${Math.round(scorePercentage)}% is below the required 85%. Try again to complete this module.`);
    // Still update progress but don't complete the module
    await updateModuleProgress(userId, moduleId, courseId, 90, scorePercentage, false);
    return false;
  }

  // Quiz passed, complete the module
  const success = await updateModuleProgress(userId, moduleId, courseId, 100, scorePercentage, true);
  if (success) {
    toast.success(`Module completed with ${Math.round(scorePercentage)}%!`);
  }
  return success;
};

// Update overall course progress with quiz validation
export const updateCourseProgress = async (
  userId: string, 
  courseId: string
): Promise<void> => {
  try {
    // First check if a course enrollment exists
    const { data: enrollment } = await supabase
      .from("user_course_enrollments")
      .select("id")
      .eq("user_id", userId)
      .eq("course_id", courseId)
      .maybeSingle();

    // Fetch all modules for this course with their completion status
    const { data: modules } = await supabase
      .from("user_module_progress")
      .select("module_id, progress")
      .eq("user_id", userId)
      .eq("course_id", courseId);

    if (!modules) return;

    // Calculate overall course progress - only count 100% completed modules
    const totalModules = modules.length;
    const completedModules = modules.filter(m => m.progress === 100).length;
    const overallProgress = totalModules > 0 
      ? Math.round((completedModules / totalModules) * 100) 
      : 0;

    // Course is only complete if ALL modules are at 100% (meaning all quizzes passed)
    const isCompleted = totalModules > 0 && completedModules === totalModules;

    if (enrollment) {
      // Update existing enrollment
      await supabase
        .from("user_course_enrollments")
        .update({
          progress: overallProgress,
          completed_at: isCompleted ? new Date().toISOString() : null
        })
        .eq("id", enrollment.id);

    }
  } catch (error) {
    console.error("Error updating course progress:", error);
  }
};

// Calculate course quiz average for NeoCertify 80+ feature
export const calculateCourseQuizAverage = async (
  userId: string,
  courseId: string
): Promise<{ 
  overallAverage: number; 
  quizBreakdown: Array<{
    moduleId: string;
    moduleName: string;
    score: number;
    maxScore: number;
    percentage: number;
    passed: boolean;
  }>;
  totalQuizzes: number;
  passedQuizzes: number;
}> => {
  try {
    // Get all PASSED quiz attempts for this course (using course_id for accuracy)
    const { data: attempts, error } = await supabase
      .from("module_attempts")
      .select("module_id, score, max_score, passed")
      .eq("user_id", userId)
      .eq("course_id", courseId)  // Changed from .like to .eq for accuracy
      .eq("passed", true)  // Only count passed attempts for certificate eligibility
      .order('attempted_at', { ascending: false });

    if (error) throw error;

    if (!attempts || attempts.length === 0) {
      return {
        overallAverage: 0,
        quizBreakdown: [],
        totalQuizzes: 0,
        passedQuizzes: 0
      };
    }

    // Get unique modules only (latest passed attempt per module)
    const uniqueModuleAttempts = new Map<string, typeof attempts[0]>();
    attempts.forEach(attempt => {
      if (!uniqueModuleAttempts.has(attempt.module_id)) {
        uniqueModuleAttempts.set(attempt.module_id, attempt);
      }
    });

    const uniqueAttempts = Array.from(uniqueModuleAttempts.values());

    // Calculate breakdown for each unique quiz
    const quizBreakdown = uniqueAttempts.map(attempt => {
      const percentage = (attempt.score / attempt.max_score) * 100;
      return {
        moduleId: attempt.module_id,
        moduleName: attempt.module_id.split('-').pop() || 'Module', // Extract module name
        score: attempt.score,
        maxScore: attempt.max_score,
        percentage: Math.round(percentage * 100) / 100, // Round to 2 decimal places
        passed: attempt.passed
      };
    });

    // Calculate overall average from unique modules only
    const totalPoints = uniqueAttempts.reduce((sum, attempt) => sum + attempt.score, 0);
    const totalMaxPoints = uniqueAttempts.reduce((sum, attempt) => sum + attempt.max_score, 0);
    const overallAverage = totalMaxPoints > 0 ? (totalPoints / totalMaxPoints) * 100 : 0;

    return {
      overallAverage: Math.round(overallAverage * 100) / 100,
      quizBreakdown,
      totalQuizzes: uniqueAttempts.length,
      passedQuizzes: uniqueAttempts.filter(a => a.passed).length
    };
  } catch (error) {
    console.error("Error calculating course quiz average:", error);
    return {
      overallAverage: 0,
      quizBreakdown: [],
      totalQuizzes: 0,
      passedQuizzes: 0
    };
  }
};

// Get detailed quiz scores for mark sheet generation
export const getCourseQuizScores = async (
  userId: string,
  courseId: string
): Promise<Array<{
  moduleId: string;
  moduleName: string;
  attemptDate: string;
  score: number;
  maxScore: number;
  percentage: number;
  passed: boolean;
  timeSpent: number;
}>> => {
  try {
    const { data: attempts, error } = await supabase
      .from("module_attempts")
      .select("module_id, score, max_score, passed, attempted_at, time_spent")
      .eq("user_id", userId)
      .eq("course_id", courseId)
      .order("attempted_at", { ascending: true });

    if (error) throw error;

    return attempts?.map(attempt => ({
      moduleId: attempt.module_id,
      moduleName: attempt.module_id.replace(`${courseId}-`, '').replace('-', ' '),
      attemptDate: new Date(attempt.attempted_at).toLocaleDateString(),
      score: attempt.score,
      maxScore: attempt.max_score,
      percentage: Math.round((attempt.score / attempt.max_score) * 100 * 100) / 100,
      passed: attempt.passed,
      timeSpent: attempt.time_spent || 0
    })) || [];
  } catch (error) {
    console.error("Error fetching course quiz scores:", error);
    return [];
  }
};

// Enhanced certificate validation for NeoCertify 80+ feature
// Phase 2: UNIVERSAL MODULE COMPLETION VALIDATION for ALL COURSES
export const validateCourseCompletionForCertificate = async (
  userId: string,
  courseId: string
): Promise<{ 
  canIssue: boolean; 
  reason?: string;
  quizAverage?: number;
  individualQuizRequirement?: boolean;
  courseAverageRequirement?: boolean;
  completedModules?: number;
  totalModules?: number;
}> => {
  try {
    console.log(`[Certificate Validation] Starting for user ${userId}, course ${courseId}`);
    
    // PHASE 2.1: Get total modules for this course from course_assignments
    const { data: assignment, error: assignmentError } = await supabase
      .from('course_assignments')
      .select('total_modules, modules_completed')
      .eq('user_id', userId)
      .eq('course_id', courseId)
      .maybeSingle();

    if (assignmentError) {
      console.error('[Certificate Validation] Error fetching assignment:', assignmentError);
      throw assignmentError;
    }

    if (!assignment) {
      return {
        canIssue: false,
        reason: "No course assignment found. Please enroll in the course first.",
        quizAverage: 0,
        individualQuizRequirement: false,
        courseAverageRequirement: false,
        completedModules: 0,
        totalModules: 0
      };
    }

    const totalModules = assignment.total_modules || 0;
    
    // PHASE 2.2: Validate total_modules is valid
    if (totalModules === 0) {
      return {
        canIssue: false,
        reason: "Course data is corrupted (0 modules). Contact administrator to fix course assignments.",
        quizAverage: 0,
        individualQuizRequirement: false,
        courseAverageRequirement: false,
        completedModules: 0,
        totalModules: 0
      };
    }

    // PHASE 2.3: Count actually completed modules (progress = 100)
    const { data: moduleProgress, error: progressError } = await supabase
      .from('user_module_progress')
      .select('module_id, progress')
      .eq('user_id', userId)
      .eq('course_id', courseId)
      .eq('progress', 100);

    if (progressError) {
      console.error('[Certificate Validation] Error fetching module progress:', progressError);
      throw progressError;
    }

    const completedModules = moduleProgress?.length || 0;

    console.log(`[Certificate Validation] Modules: ${completedModules}/${totalModules} completed`);

    // PHASE 2.4: CRITICAL - Check if ALL modules are completed
    if (completedModules < totalModules) {
      return {
        canIssue: false,
        reason: `Complete all ${totalModules} modules. Currently: ${completedModules}/${totalModules} modules completed.`,
        quizAverage: 0,
        individualQuizRequirement: false,
        courseAverageRequirement: false,
        completedModules,
        totalModules
      };
    }

    // PHASE 2.5: Get quiz average data
    const quizData = await calculateCourseQuizAverage(userId, courseId);
    
    console.log(`[Certificate Validation] Quizzes: ${quizData.totalQuizzes} attempted, avg: ${quizData.overallAverage}%`);

    // PHASE 2.6: CRITICAL - Check if ALL modules have quiz attempts
    if (quizData.totalQuizzes < totalModules) {
      return {
        canIssue: false,
        reason: `Complete quizzes for all modules. Currently: ${quizData.totalQuizzes}/${totalModules} quizzes completed.`,
        quizAverage: quizData.overallAverage,
        individualQuizRequirement: false,
        courseAverageRequirement: false,
        completedModules,
        totalModules
      };
    }

    // PHASE 2.7: Check individual quiz requirement (85% on each quiz)
    const individualQuizRequirement = quizData.quizBreakdown.every(quiz => quiz.percentage >= 85);
    
    // PHASE 2.8: Check course average requirement (80% overall)
    const courseAverageRequirement = quizData.overallAverage >= 80;

    // PHASE 2.9: FINAL VALIDATION - ALL requirements must be met
    const canIssue = 
      completedModules === totalModules &&
      quizData.totalQuizzes === totalModules &&
      individualQuizRequirement && 
      courseAverageRequirement;

    let reason = "";
    if (!canIssue) {
      if (completedModules < totalModules) {
        reason = `Complete all ${totalModules} modules (${completedModules}/${totalModules} done)`;
      } else if (quizData.totalQuizzes < totalModules) {
        reason = `Complete all module quizzes (${quizData.totalQuizzes}/${totalModules} done)`;
      } else if (!individualQuizRequirement && !courseAverageRequirement) {
        reason = `Requires 85% on each quiz AND 80% course average. Current: ${quizData.overallAverage.toFixed(1)}% average`;
      } else if (!individualQuizRequirement) {
        reason = "Some quizzes scored below 85%. All quizzes must score 85% or higher";
      } else if (!courseAverageRequirement) {
        reason = `Course average below 80%. Current: ${quizData.overallAverage.toFixed(1)}%`;
      }
    }

    console.log(`[Certificate Validation] Result: canIssue=${canIssue}, reason=${reason}`);

    return { 
      canIssue,
      reason: canIssue ? undefined : reason,
      quizAverage: quizData.overallAverage,
      individualQuizRequirement,
      courseAverageRequirement,
      completedModules,
      totalModules
    };
  } catch (error) {
    console.error("[Certificate Validation] Error:", error);
    return { 
      canIssue: false, 
      reason: "Error validating completion",
      quizAverage: 0,
      individualQuizRequirement: false,
      courseAverageRequirement: false,
      completedModules: 0,
      totalModules: 0
    };
  }
};

export const enrollInCourse = async (
  userId: string, 
  courseId: string
): Promise<boolean> => {
  try {
    // Check if already enrolled
    const { data: enrollment } = await supabase
      .from("user_course_enrollments")
      .select("id")
      .eq("user_id", userId)
      .eq("course_id", courseId)
      .maybeSingle();

    if (!enrollment) {
      // If not enrolled, create enrollment
      const { error } = await supabase
        .from("user_course_enrollments")
        .insert({
          user_id: userId,
          course_id: courseId,
          progress: 0
        });

      if (error) throw error;
      
      toast.success("Successfully enrolled in course");
    } else {
      // Update last accessed time
      await supabase
        .from("user_course_enrollments")
        .update({
          last_accessed_at: new Date().toISOString()
        })
        .eq("id", enrollment.id);
    }
    
    return true;
  } catch (error) {
    console.error("Error enrolling in course:", error);
    toast.error("Failed to enroll in course");
    return false;
  }
};
